// =============================================================================
//  FACTURE SIMPLIFIÉE — profil de facturation « renovation » (Mario Bat) uniquement.
// =============================================================================
//  Branchée par UNE condition dans FacturesDomaine (profil_facturation ===
//  "renovation" et nature ≠ avoir). Les autres profils (OMV, TEF, Climat…) gardent le détail
//  standard, inchangé.
//
//  Par défaut : lecture simple (client, lignes, montants) + deux actions,
//  « Modifier » et « Émettre la facture ». Tout le technique (correction du
//  total, avertissements catalogue, cycle, gel, avoirs, relations) reste
//  ACCESSIBLE : zone Modifier, ou « Options avancées » (= détail standard).
//
//  Le serveur décide, l'écran obéit : `modifiable`, `emission_possible`,
//  `motif_emission`, `motif_etat`, `pdf_disponible`. Aucune règle légale n'est
//  rejouée ici ; l'émission passe par la MÊME route que le détail standard.
//
//  Zones de « Modifier » : chaque zone est une section autonome. La quantité
//  n'est proposée que si le serveur l'autorise (`quantite_modifiable`) ; elle
//  passe par sa route dédiée (motif obligatoire, tracée). Les coordonnées du
//  client ne s'éditent PAS ici : un lien ouvre la fiche client existante.
// =============================================================================
import { useEffect, useState } from "react";
import { FileText, Download, Loader2, ArrowLeft, Pencil, Save, Check,
         ChevronDown, ChevronRight, Lock } from "lucide-react";
import { BlocPaiement, BoutonEnvoi, TraceEnvoi } from "./FactureReglement";
import { AnnulationFacture, AvoirsEtSolde, FormEchelonnement, LIBELLE_TYPE, euros as eurosLot } from "./EchelonnementFacture";

const etiquette = { fontSize: 12, color: "var(--oh-text-secondary)", fontWeight: 500 };
const carte = {
  background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
  borderRadius: 12, padding: 16,
};
const champ = {
  height: 36, borderRadius: 8, padding: "0 12px", fontSize: 13,
  background: "var(--oh-bg-surface)", color: "var(--oh-text)",
  border: "1px solid var(--oh-border)", width: "100%",
};
const btnBase = {
  display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 12px",
  borderRadius: 8, fontSize: 13, fontWeight: 500, minHeight: 36,
};
const btnPrimaire = {
  ...btnBase, border: "none", background: "var(--oh-accent-solid)",
  color: "var(--oh-accent-contrast)",
};
const btnSecondaire = {
  ...btnBase, background: "transparent", border: "1px solid var(--oh-accent)",
  color: "var(--oh-accent-text)",
};
const btnDiscret = {
  ...btnBase, background: "transparent", border: "1px solid var(--oh-border)",
  color: "var(--oh-text-secondary)",
};
const nombre = { fontVariantNumeric: "tabular-nums" };

const STATUTS = {
  brouillon: { bg: "var(--oh-stat-amber-bg)", text: "var(--oh-stat-amber-text)", libelle: "Brouillon" },
  emise: { bg: "var(--oh-stat-blue-bg)", text: "var(--oh-stat-blue-text)", libelle: "Émise" },
  payee: { bg: "var(--oh-stat-green-bg)", text: "var(--oh-stat-green-text)", libelle: "Payée" },
  paid: { bg: "var(--oh-stat-green-bg)", text: "var(--oh-stat-green-text)", libelle: "Payée" },
  annulee: { bg: "var(--oh-stat-red-bg)", text: "var(--oh-stat-red-text)", libelle: "Annulée" },
};

function euros(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function dateFR(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR");
}

function Statut({ statut, libelle }) {
  const t = STATUTS[String(statut || "").toLowerCase()]
    || { bg: "var(--oh-bg-surface)", text: "var(--oh-text-secondary)", libelle: statut || "—" };
  return (
    <span style={{ padding: "4px 8px", borderRadius: 8, fontSize: 12,
                   background: t.bg, color: t.text, whiteSpace: "nowrap" }}>
      {libelle || t.libelle}
    </span>
  );
}

// Défini hors du composant parent : un composant redéfini à chaque rendu est
// remonté à chaque frappe et le champ perd le focus (cf. MoneyInput).
function PrixLigne({ ligne, motif, occupe, onEnregistrer }) {
  const [prix, setPrix] = useState(String(ligne.prix_unitaire_ht ?? ""));
  useEffect(() => { setPrix(String(ligne.prix_unitaire_ht ?? "")); }, [ligne.prix_unitaire_ht]);
  const change = prix !== "" && Number(prix) !== Number(ligne.prix_unitaire_ht);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
      <div style={{ flex: "0 0 160px" }}>
        <label style={etiquette} htmlFor={`prix-${ligne.id}`}>Prix unitaire HT</label>
        <input id={`prix-${ligne.id}`} type="number" step="0.01" min="0" style={champ}
               data-testid={`simple-prix-${ligne.id}`} value={prix} disabled={occupe}
               onChange={(e) => setPrix(e.target.value)} />
      </div>
      <button type="button" style={btnDiscret} data-testid={`simple-prix-appliquer-${ligne.id}`}
              disabled={occupe || !change || !motif.trim()}
              aria-label={`Enregistrer le prix de ${ligne.designation || "la ligne"}`}
              onClick={() => onEnregistrer(ligne.id, Number(prix))}>
        <Save style={{ width: 14, height: 14 }} /> Enregistrer le prix
      </button>
    </div>
  );
}

function QuantiteLigne({ ligne, motif, occupe, onEnregistrer }) {
  const [quantite, setQuantite] = useState(String(ligne.quantite ?? ""));
  useEffect(() => { setQuantite(String(ligne.quantite ?? "")); }, [ligne.quantite]);
  const change = quantite !== "" && Number(quantite) > 0 && Number(quantite) !== Number(ligne.quantite);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
      <div style={{ flex: "0 0 160px" }}>
        <label style={etiquette} htmlFor={`quantite-${ligne.id}`}>
          Quantité{ligne.unite ? ` (${ligne.unite})` : ""}
        </label>
        <input id={`quantite-${ligne.id}`} type="number" step="any" min="0" style={champ}
               data-testid={`simple-quantite-${ligne.id}`} value={quantite} disabled={occupe}
               onChange={(e) => setQuantite(e.target.value)} />
      </div>
      <button type="button" style={btnDiscret} data-testid={`simple-quantite-appliquer-${ligne.id}`}
              disabled={occupe || !change || !motif.trim()}
              aria-label={`Enregistrer la quantité de ${ligne.designation || "la ligne"}`}
              onClick={() => onEnregistrer(ligne.id, Number(quantite))}>
        <Save style={{ width: 14, height: 14 }} /> Enregistrer la quantité
      </button>
    </div>
  );
}

function TotalHT({ facture, motif, occupe, onEnregistrer }) {
  const [total, setTotal] = useState("");
  const change = total !== "" && Number(total) !== Number(facture.total_ht);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
      <div style={{ flex: "0 0 160px" }}>
        <label style={etiquette} htmlFor="simple-total-ht">Total HT</label>
        <input id="simple-total-ht" type="number" step="0.01" style={champ}
               data-testid="simple-total-ht" value={total} disabled={occupe}
               placeholder={String(facture.total_ht ?? "")}
               onChange={(e) => setTotal(e.target.value)} />
      </div>
      <button type="button" style={btnDiscret} data-testid="simple-total-appliquer"
              disabled={occupe || !change || !motif.trim()}
              onClick={async () => { if (await onEnregistrer(Number(total))) setTotal(""); }}>
        <Save style={{ width: 14, height: 14 }} /> Corriger le total
      </button>
    </div>
  );
}

function Zone({ id, titre, children }) {
  return (
    <section style={{ display: "grid", gap: 12 }} data-testid={`simple-zone-${id}`}
             aria-label={titre}>
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--oh-text)" }}>{titre}</h3>
      {children}
    </section>
  );
}

export default function FactureSimple({ facture, catalogue, erreur, occupe, onRetour,
                                        onEmettre, onAgir, onCorrigerProduit, onVoirPdf, onTelecharger,
                                        detailStandard, SelecteurProduit, onOuvrirClient }) {
  const f = facture;
  const [edition, setEdition] = useState(false);
  const [avance, setAvance] = useState(false);
  const [motif, setMotif] = useState("");
  const lignes = f.lignes || [];
  const peutModifier = f.modifiable === true;
  const sommeLignes = lignes.reduce((t, l) => t + (Number(l.montant_ht) || 0), 0);
  const ecartTotaux = lignes.length > 0 && f.total_ht != null
    && Math.abs(sommeLignes - Number(f.total_ht)) >= 0.005;

  const ech = f.echelonnement;
  const typesEchelonnes = ech ? [
    ...(ech.pourcentage_restant > 0 ? ["acompte", "intermediaire"] : []),
    ...((ech.acomptes || []).length ? ["solde"] : [])] : [];

  // Une facture figée (émise) ne se modifie plus : la zone se referme d'elle-même.
  useEffect(() => { if (!peutModifier) setEdition(false); }, [peutModifier]);

  const enregistrerPrix = async (ligneId, prix) => {
    const fait = await onAgir(`/${f.id}/lignes/${ligneId}/prix`,
                              { prix_unitaire_ht: prix, motif: motif.trim() });
    if (fait) setMotif("");
    return fait;
  };
  const enregistrerQuantite = async (ligneId, quantite) => {
    const fait = await onAgir(`/${f.id}/lignes/${ligneId}/quantite`,
                              { quantite, motif: motif.trim() });
    if (fait) setMotif("");
    return fait;
  };
  const enregistrerTotal = async (total) => {
    const fait = await onAgir(`/${f.id}`, { changements: { total_ht: total },
                                            motif: motif.trim() }, "PATCH");
    if (fait) setMotif("");
    return fait;
  };

  return (
    <div style={{ display: "grid", gap: 16 }} data-testid="facture-simple">
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button type="button" style={btnDiscret} data-testid="simple-retour" onClick={onRetour}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Retour
        </button>
        <span style={{ fontSize: 16, fontWeight: 500, color: "var(--oh-text)" }}>
          {LIBELLE_TYPE[f.type_facture] || "Facture"}{f.numero ? ` ${f.numero}` : ""}
          {f.pourcentage_acompte ? ` (${f.pourcentage_acompte} %)` : ""}
        </span>
        <Statut statut={f.statut} libelle={f.statut_libelle} />
      </div>

      {erreur && (
        <div style={{ ...carte, background: "var(--oh-stat-red-bg)",
                      color: "var(--oh-stat-red-text)", fontSize: 13 }}
             role="alert" data-testid="simple-erreur">{erreur}</div>
      )}

      <div style={{ ...carte, display: "grid", gap: 12,
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
           data-testid="simple-infos">
        <div><p style={etiquette}>Client</p>
          <p style={{ margin: 0, color: "var(--oh-text)" }}>{f.client_nom || "—"}</p>
          {f.client_id && onOuvrirClient && (
            <button type="button" style={{ ...btnDiscret, marginTop: 8 }} data-testid="simple-fiche-client"
                    onClick={() => onOuvrirClient(f.client_id)}>
              Voir la fiche client
            </button>
          )}</div>
        {f.date_emission && (
          <div><p style={etiquette}>Émise le</p>
            <p style={{ margin: 0, color: "var(--oh-text)" }}>{dateFR(f.date_emission)}</p></div>
        )}
        <div><p style={etiquette}>Échéance</p>
          <p style={{ margin: 0, color: "var(--oh-text)" }}>{dateFR(f.date_echeance)}</p></div>
        {(f.devis_numero || f.devis_id) && (
          <div><p style={etiquette}>Devis d'origine</p>
            <p style={{ margin: 0, color: "var(--oh-text)" }}>{f.devis_numero || f.devis_id}</p></div>
        )}
      </div>

      <div style={carte} data-testid="simple-lignes">
        <p style={{ ...etiquette, marginBottom: 8 }}>Produits et prestations</p>
        <div style={{ display: "grid", gap: 12 }}>
          {lignes.map((l, i) => (
            <div key={l.id || i} data-testid={`simple-ligne-${l.id || i}`}
                 style={{ display: "flex", gap: 12, justifyContent: "space-between",
                          flexWrap: "wrap", color: "var(--oh-text)", fontSize: 13 }}>
              <div style={{ flex: "1 1 240px" }}>
                <div>{l.designation}</div>
                {l.commentaire_ligne && (
                  <div style={{ ...etiquette, marginTop: 4 }}>{l.commentaire_ligne}</div>
                )}
              </div>
              <div style={{ ...nombre, textAlign: "right" }}>
                <div style={etiquette}>{l.quantite}{l.unite ? ` ${l.unite}` : ""} × {euros(l.prix_unitaire_ht)}</div>
                <div>{euros(l.montant_ht)}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ borderTop: "0.5px solid var(--oh-border)", marginTop: 12, paddingTop: 12,
                      display: "grid", gap: 4, justifyContent: "end", fontSize: 13,
                      color: "var(--oh-text)", ...nombre }}
             data-testid="simple-totaux">
          <div style={{ display: "flex", gap: 24, justifyContent: "space-between" }}>
            <span style={etiquette}>Total HT</span><span>{euros(f.total_ht)}</span></div>
          <div style={{ display: "flex", gap: 24, justifyContent: "space-between" }}>
            <span style={etiquette}>TVA</span><span>{euros(f.total_tva)}</span></div>
          <div style={{ display: "flex", gap: 24, justifyContent: "space-between", fontWeight: 500 }}>
            <span style={etiquette}>Total TTC</span><span>{euros(f.total_ttc)}</span></div>
        </div>
      </div>

      <AvoirsEtSolde facture={f} />

      {f.echelonnement_possible && f.echelonnement && f.nature !== "avoir" && (
        <div style={{ ...carte, display: "grid", gap: 12 }} data-testid="simple-echelonnement">
          <p style={{ ...etiquette, margin: 0 }}>Facturation échelonnée</p>
          {f.imputation_provisoire === true && (
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "var(--oh-stat-amber-text)" }}
               data-testid="simple-imputation-provisoire">
              Imputation des acomptes provisoire : elle est recalculée à l'émission puis figée.
            </p>
          )}
          {(f.echelonnement.acomptes || []).map((a) => (
            <p key={a.id} style={{ margin: 0, fontSize: 13, color: "var(--oh-text)", ...nombre }}
               data-testid={`simple-acompte-${a.id}`}>
              {LIBELLE_TYPE[a.type_facture] || "Acompte"} {a.numero || "brouillon"} · {a.pourcentage} % · {eurosLot(a.total_ttc)}
            </p>
          ))}
          <p style={{ margin: 0, fontSize: 13, color: "var(--oh-text)", ...nombre }} data-testid="simple-pourcentage-restant">
            Part du devis déjà facturée en acompte : {f.echelonnement.pourcentage_facture} % — reste {f.echelonnement.pourcentage_restant} %
          </p>
          {f.devis_id && !f.echelonnement.solde_existant && typesEchelonnes.length > 0 && (
            <FormEchelonnement key={typesEchelonnes.join("-")} types={typesEchelonnes}
              pourcentageRestant={f.echelonnement.pourcentage_restant} occupe={occupe}
              onCreer={(type, pourcentage) => onAgir("/depuis-devis",
                { devis_id: f.devis_id, type_facture: type, ...(pourcentage != null ? { pourcentage } : {}) })} />
          )}
        </div>
      )}

      {ecartTotaux && (
        <div style={{ ...carte, fontSize: 13, lineHeight: 1.6,
                      background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}
             role="alert" data-testid="simple-totaux-ecart">
          Les totaux ne correspondent pas à la somme des lignes : les lignes font {euros(sommeLignes)} hors
          taxes, la facture indique {euros(f.total_ht)}. Vérifiez les montants avant d'émettre ou d'envoyer
          cette facture.
        </div>
      )}

      {!peutModifier && f.motif_etat && (
        <div style={{ ...carte, fontSize: 13, lineHeight: 1.6,
                      background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}
             data-testid="simple-motif-etat">
          <Lock style={{ width: 14, height: 14, verticalAlign: "-2px" }} /> {f.motif_etat}
        </div>
      )}

      {!f.emission_possible && f.motif_emission && (
        <p style={{ ...etiquette, margin: 0, lineHeight: 1.6 }} data-testid="simple-motif-emission">
          {f.motif_emission}
        </p>
      )}

      <BlocPaiement facture={f} occupe={occupe} onAgir={onAgir} />

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {f.emission_possible && (
          <button type="button" style={{ ...btnPrimaire, cursor: occupe ? "wait" : "pointer" }}
                  data-testid="simple-emettre" disabled={occupe} onClick={() => onEmettre(f)}>
            {occupe ? <Loader2 style={{ width: 14, height: 14 }} />
                    : <FileText style={{ width: 14, height: 14 }} />}
            Émettre la facture
          </button>
        )}
        {f.avoir_possible && f.nature !== "avoir" && !(f.solde && f.solde.soldee) && (
          <AnnulationFacture facture={f} occupe={occupe} onAgir={onAgir} />
        )}
        {peutModifier && (
          <button type="button" style={btnSecondaire} data-testid="simple-modifier"
                  aria-expanded={edition} disabled={occupe}
                  onClick={() => setEdition((v) => !v)}>
            {edition ? <Check style={{ width: 14, height: 14 }} />
                     : <Pencil style={{ width: 14, height: 14 }} />}
            {edition ? "Terminer la modification" : "Modifier"}
          </button>
        )}
        {f.pdf_disponible !== false && (
          <button type="button" style={btnDiscret} data-testid="simple-pdf"
                  onClick={() => onVoirPdf(f)}>
            <Download style={{ width: 14, height: 14 }} /> Voir le PDF
          </button>
        )}
        {f.pdf_disponible !== false && onTelecharger && (
          <button type="button" style={btnSecondaire} data-testid="simple-telecharger"
                  onClick={() => onTelecharger(f)}>
            <Download style={{ width: 14, height: 14 }} /> Télécharger
          </button>
        )}
        <BoutonEnvoi facture={f} occupe={occupe} onAgir={onAgir} />
      </div>
      <TraceEnvoi facture={f} />

      {edition && peutModifier && (
        <div style={{ ...carte, display: "grid", gap: 24 }} data-testid="simple-edition">
          <Zone id="produits" titre="Produits et prestations">
            {catalogue?.avertissement && (
              <p style={{ ...etiquette, margin: 0, lineHeight: 1.6,
                          color: "var(--oh-stat-amber-text)" }}
                 data-testid="simple-catalogue-avertissement">{catalogue.avertissement}</p>
            )}
            {lignes.filter((l) => l.type_ligne === "produit").map((l) => (
              <div key={l.id} style={{ display: "grid", gap: 4 }}
                   data-testid={`simple-produit-${l.id}`}>
                <div style={{ fontSize: 13, color: "var(--oh-text)" }}>{l.designation}</div>
                <div style={etiquette}>Référence : {l.reference || "—"}</div>
                <SelecteurProduit ligne={l} facture={f} catalogue={catalogue}
                                  occupe={occupe} surCorrection={onCorrigerProduit} />
              </div>
            ))}
          </Zone>

          <Zone id="montants" titre="Montants">
            <div>
              <label style={etiquette} htmlFor="simple-motif">
                Motif de la modification (obligatoire)
              </label>
              <input id="simple-motif" style={champ} data-testid="simple-motif"
                     placeholder="Pourquoi ce changement ?" value={motif} disabled={occupe}
                     onChange={(e) => setMotif(e.target.value)} />
              <p style={{ ...etiquette, marginTop: 4 }}>
                Chaque modification est tracée (avant, après, auteur, motif).
              </p>
            </div>
            {lignes.map((l) => (
              <div key={l.id} style={{ display: "grid", gap: 4 }}>
                <div style={{ fontSize: 13, color: "var(--oh-text)" }}>
                  {l.designation}
                  <span style={{ ...etiquette, marginLeft: 8, ...nombre }}>
                    {l.quantite}{l.unite ? ` ${l.unite}` : ""}
                  </span>
                </div>
                {f.quantite_modifiable === true && l.type_ligne !== "titre" && l.type_ligne !== "aide_deduite" && (
                  <QuantiteLigne ligne={l} motif={motif} occupe={occupe}
                                 onEnregistrer={enregistrerQuantite} />
                )}
                <PrixLigne ligne={l} motif={motif} occupe={occupe}
                           onEnregistrer={enregistrerPrix} />
              </div>
            ))}
            {f.nature !== "avoir" && (
              <TotalHT facture={f} motif={motif} occupe={occupe}
                       onEnregistrer={enregistrerTotal} />
            )}
          </Zone>
        </div>
      )}

      <div>
        <button type="button" style={btnDiscret} data-testid="simple-avance-bascule"
                aria-expanded={avance} onClick={() => setAvance((v) => !v)}>
          {avance ? <ChevronDown style={{ width: 14, height: 14 }} />
                  : <ChevronRight style={{ width: 14, height: 14 }} />}
          Options avancées
        </button>
        {avance && (
          <div style={{ marginTop: 12 }} data-testid="simple-avance">{detailStandard}</div>
        )}
      </div>
    </div>
  );
}
